import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/Reveal";
import { Icon } from "@/components/icons";
import CTASection from "@/components/CTASection";
import { IMAGES, ABOUT, STATS, CORE_VALUES, CERTIFICATIONS, MEMBERSHIPS } from "@/lib/data";

const WhoWeAre = () => (
  <section className="bg-white" data-testid="about-who">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-14 items-start">
        <Reveal>
          <Label>Who We Are</Label>
          <h2 className="font-heading text-3xl md:text-5xl uppercase font-bold text-navy tracking-tight mt-4 leading-[1.05]">
            Building better through science.
          </h2>
          <div className="overflow-hidden mt-8">
            <img src={IMAGES.aboutInterior} alt="Building interior" className="w-full h-[320px] object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-lg text-navy/75 leading-relaxed">{ABOUT.whoWeAre}</p>
          <h3 className="font-heading uppercase text-navy font-semibold mt-9 text-lg tracking-wide">Our Expertise</h3>
          <div className="w-10 h-[2px] bg-brand my-3" />
          <p className="text-navy/70 leading-relaxed">{ABOUT.ourExpertise}</p>
          <h3 className="font-heading uppercase text-navy font-semibold mt-8 text-lg tracking-wide">Why Choose Us</h3>
          <div className="w-10 h-[2px] bg-brand my-3" />
          <p className="text-navy/70 leading-relaxed">{ABOUT.whyChooseUs}</p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border mt-16">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div className="p-8 border-r border-b border-border" data-testid={`stat-${i}`}>
              <div className="font-heading text-4xl md:text-5xl font-bold text-brand tracking-tight">{s.value}</div>
              <div className="text-xs tracking-[0.15em] uppercase text-navy/60 mt-2 font-semibold">{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Values = () => (
  <section className="bg-secondary" data-testid="about-values">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <Reveal className="text-center max-w-2xl mx-auto mb-16">
        <Label>What Drives Us</Label>
        <h2 className="font-heading text-4xl md:text-5xl uppercase font-bold text-navy tracking-tight mt-4">Our Core Values</h2>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
        {CORE_VALUES.map((v, i) => (
          <Reveal key={v.title} delay={(i % 4) * 0.06}>
            <div className="group h-full p-8 border-r border-b border-border hover:bg-brand-light transition-colors bg-white" data-testid={`value-${i}`}>
              <Icon name={v.icon} className="w-9 h-9 text-navy group-hover:text-brand transition-colors" />
              <h3 className="font-heading uppercase text-navy font-semibold mt-5 text-base leading-tight">{v.title}</h3>
              <div className="w-8 h-[2px] bg-brand my-3" />
              <p className="text-sm text-navy/60 leading-relaxed">{v.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Credentials = () => (
  <section className="bg-white" data-testid="about-credentials">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
      <div className="grid lg:grid-cols-2 gap-14">
        <Reveal>
          <Label>Certifications</Label>
          <h2 className="font-heading text-3xl md:text-4xl uppercase font-bold text-navy tracking-tight mt-4">Quality you can trust.</h2>
          <img
            src={`${process.env.PUBLIC_URL}/oqm-certified.png`}
            alt="OQM Certified — Engineers & Geoscientists British Columbia"
            className="mt-8 h-20 w-auto"
            data-testid="oqm-logo"
          />
          <div className="mt-8 space-y-6">
            {CERTIFICATIONS.map((c) => (
              <div key={c.title} className="border-l-2 border-brand pl-5" data-testid={`cert-${c.title}`}>
                <h3 className="font-heading uppercase text-navy font-semibold text-lg">{c.title}</h3>
                <p className="text-navy/65 leading-relaxed mt-2">{c.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Label>Memberships & Affiliations</Label>
          <h2 className="font-heading text-3xl md:text-4xl uppercase font-bold text-navy tracking-tight mt-4">Proud members of.</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {MEMBERSHIPS.map((m, i) => (
              <div key={m.abbr} className="border border-border p-5 hover:border-brand transition-colors flex items-center gap-4" data-testid={`membership-${i}`}>
                <span className="font-heading font-bold text-brand text-xl shrink-0">{m.abbr}</span>
                <span className="text-sm text-navy/70 leading-tight">{m.full}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <PageHero
        label="About AUM"
        titleLines={["Science.", "Performance.", "Sustainability."]}
        description={ABOUT.intro}
        image={IMAGES.aboutHero}
        imageAlt="AUM building"
      />
      <WhoWeAre />
      <Values />
      <Credentials />
      <CTASection />
    </>
  );
}
